import {
    IDrivingStore,
    IDrivingVehicle
} from 'components/driving/driving-interface'
import ModalComponent from 'components/main/modal'
import { initValues, PostmediaStorage } from 'constants/enums'
import { ApiService } from 'services/api/api.service'
import { SessionStore } from 'services/store'
import { ComparisonResults } from './car-comparison/comparison-results'
import { isArrayEqual } from 'utils/array'
import { logger } from 'utils/system/logger'
import { CompareEventType, MAX_VEHICLES } from './car-comparison/constants'
import { MyPicks } from './car-comparison/my-picks'
import { ModelCard } from './model-card/model-card'
import { TrimSelector } from './model-card/trim-selector'
import { VehicleFinderComponent } from './vehicle-finder/vehicle-finder.component'

/**
 * Driving car comparison session store controller
 */
export class CarComparisonStore {
    public trimSelectorModal: TrimSelector
    public limitExceededModal: ModalComponent
    public noMorePickModal: ModalComponent
    public modelCards: ModelCard[] = []
    public lastActiveModelCard: ModelCard
    public vehicleFinder: VehicleFinderComponent
    public myPicks: MyPicks

    /**
     * Singleton pattern
     */
    private static _instance: CarComparisonStore
    public static getInstance(): CarComparisonStore {
        return this._instance || (this._instance = new this())
    }

    /**
     * Add vehicle to the store
     *
     * @param {number} id Vehicle to add
     * @param {IDrivingModel} model Vehicle model
     */
    public async add(id: number): Promise<void> {
        let vehicles = this.get()

        // Exceeded the limit. Send event and do nothing.
        if (vehicles.length >= MAX_VEHICLES) {
            this.update(
                vehicles,
                CompareEventType.DRIVING_COMPARE_ADD_OUT_OF_RANGE,
                undefined
            )
            return
        }

        // Existing vehicle. Do nothing.
        const exist = vehicles.reduce((indicator: boolean, vehicle) => {
            if (vehicle.id === id) {
                return true
            }
            return indicator
        }, false)
        if (exist) {
            return
        }

        // Store loading indicator and Send pre-event
        vehicles.push({
            id,
            isLoading: true
        })
        this.update(vehicles, CompareEventType.DRIVING_COMPARE_ADD_INIT, id)

        await new ApiService()
            .fetchDrivingVehicle(id)
            .then((vehicle: IDrivingVehicle) => {
                vehicles = this.get()
                // Update the vehicle
                vehicles = vehicles.map((item) => {
                    if (item.id === id) {
                        return {
                            ...item,
                            isLoading: false,
                            item: vehicle
                        }
                    }
                    return item
                })

                // Store
                this.update(
                    vehicles,
                    CompareEventType.DRIVING_COMPARE_ADD_SUCCESS,
                    vehicle
                )

                // Sponsored Vehicle
                void this.loadSponsoredVehicle(vehicles)
            })
            .catch(() => {
                vehicles = this.get()
                this.update(
                    vehicles.filter((vehicle) => vehicle.id !== id),
                    CompareEventType.DRIVING_COMPARE_ADD_FAILED,
                    id
                )
            })
    }

    /**
     * Remove vehicle by its id
     *
     * @param {number} id Vehicle to remove
     */
    public remove(id: number): void {
        // Remove
        const vehicles = this.get().filter((vehicle) => vehicle.id !== id)
        this.update(vehicles, CompareEventType.DRIVING_COMPARE_REMOVE, id)

        // Sponsored Vehicle
        void this.loadSponsoredVehicle(vehicles)
    }

    /**
     * Get stored vehicles
     *
     * @return {IDrivingStore<IDrivingVehicle>[]}
     */
    public get(): IDrivingStore<IDrivingVehicle>[] {
        return SessionStore.getInstance().get(PostmediaStorage.CAR_COMPARISON)
    }

    /**
     * Build comparison results full url path based on comparison session
     *
     * @returns {string}
     */
    public buildPath(): string {
        const vehicles = this.get()
        const defaultPath = ComparisonResults.pageUrl
        if (vehicles.length) {
            const idPath = vehicles
                .map((vehicle: IDrivingStore<IDrivingVehicle>) => {
                    if (!vehicle.item) {
                        return []
                    }
                    return vehicle.item.id
                })
                .join('-')
            const namePath = vehicles
                .map((vehicle: IDrivingStore<IDrivingVehicle>) => {
                    if (!vehicle.item) {
                        return []
                    }
                    if (vehicle.item.slugified_name === '-') {
                        vehicle.item.slugified_name = 'base-trim'
                    }
                    return `${vehicle.item.slugified_full_model_name}-${vehicle.item.slugified_name}`
                })
                .join('-vs-')
            if (idPath && namePath) {
                return `${defaultPath}/${idPath}/${namePath}`
            }
        }
        return `${defaultPath}/`
    }

    /**
     * Store data and send event
     *
     * @param {IDrivingStore<IDrivingVehicle>} vehicles
     * @param {string} event
     */
    private update(
        vehicles: IDrivingStore<IDrivingVehicle>[],
        event: string,
        detail: unknown
    ): void {
        // Store
        SessionStore.getInstance().set(
            PostmediaStorage.CAR_COMPARISON,
            vehicles
        )

        // Send event
        document.dispatchEvent(new CustomEvent(event, { detail }))
    }

    public registerModelCard(component: ModelCard): void {
        this.modelCards.push(component)
    }

    public updateModelCardsState(): void {
        this.modelCards.forEach((card) => card.setCardState())
    }

    public saveLastActiveCard(component: ModelCard): void {
        this.lastActiveModelCard = component
    }

    /**
     * Get trim selector modal
     */
    public getTrimSelector(): TrimSelector {
        return (
            this.trimSelectorModal ||
            (this.trimSelectorModal = new TrimSelector('trim-selector-modal'))
        )
    }

    public getLimitExceededModal(): ModalComponent {
        return (
            this.limitExceededModal ||
            (this.limitExceededModal = new ModalComponent(
                undefined,
                'limit-exceeded-modal'
            ))
        )
    }

    public getNoMorePickModal(): ModalComponent {
        return (
            this.noMorePickModal ||
            (this.noMorePickModal = new ModalComponent(
                undefined,
                'no-more-pick-modal'
            ))
        )
    }

    /**
     * Get the sponsored vehicle from API
     *
     * @param {IDrivingStore<IDrivingVehicle>[]} vehicles
     * @return {Promise<void>}
     */
    private async loadSponsoredVehicle(
        vehicles: IDrivingStore<IDrivingVehicle>[]
    ): Promise<void> {
        // Reset if vehicle is empty
        if (!vehicles.length) {
            SessionStore.getInstance().set(
                PostmediaStorage.SPONSORED_VEHICLE,
                initValues[PostmediaStorage.SPONSORED_VEHICLE]
            )
            this.myPicks.renderSponsoredVehicle()
            return
        }

        const ids = vehicles
            .map((item) => (item.item ? item.item.id : 0))
            .filter((value) => value)
        const bodystyles = vehicles
            .map((item) => (item.item ? item.item.bodystyle : ''))
            .filter((value) => value)
        const msrps = vehicles
            .map((item) => (item.item ? item.item.msrp : 0))
            .filter((value) => value)

        await new ApiService()
            .fetchDrivingSponsoredVehicle({
                vehicle_ids: ids,
                bodystyles,
                msrps
            })
            .then((vehicle: IDrivingVehicle) => {
                SessionStore.getInstance().set(
                    PostmediaStorage.SPONSORED_VEHICLE,
                    { ids, item: vehicle }
                )
                this.myPicks.renderSponsoredVehicle(vehicle)
            })
            .catch((e) => logger('error', e))
    }
    /**
     * Init sponsored vehicle
     * When MyPicks and Sponsored are matched, render the section
     * When they are not matched, re-load sponsored
     */
    public initSponsoredVehicle(): void {
        const vehicles = this.get()
        const vehicleIds = vehicles.map((vehicle) => vehicle.id)
        const sponsored = SessionStore.getInstance().get(
            PostmediaStorage.SPONSORED_VEHICLE
        )
        if (isArrayEqual(vehicleIds, sponsored.ids)) {
            this.myPicks.renderSponsoredVehicle(sponsored.item)
            return
        }
        void this.loadSponsoredVehicle(vehicles)
    }
}
